import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Available Claude models
export type ClaudeModel = 'claude-haiku-4-5-20251001' | 'claude-sonnet-4-20250514' | 'claude-opus-4-20250514'

// Coaching styles (legacy - kept for backward compatibility)
export type CoachingStyle = 'directive' | 'collaborative'

export interface CoachingStyleOption {
  id: CoachingStyle
  name: string
  description: string
}

export const COACHING_STYLES: CoachingStyleOption[] = [
  {
    id: 'directive',
    name: 'Classic',
    description: 'Direct questions with quality assessment',
  },
  {
    id: 'collaborative',
    name: 'Collaborative',
    description: 'Warm coaching with frameworks & scaffolds',
  },
]

// Conversation modes (new conversational approach)
export type ConversationMode = 'guided' | 'organic'

export interface ConversationModeOption {
  id: ConversationMode
  name: string
  description: string
}

export const CONVERSATION_MODES: ConversationModeOption[] = [
  {
    id: 'organic',
    name: 'Free Flow',
    description: 'Natural conversation, topics emerge organically',
  },
  {
    id: 'guided',
    name: 'Guided',
    description: 'AI suggests which topics to explore next',
  },
]

export interface ModelOption {
  id: ClaudeModel
  name: string
  description: string
  speed: 'fast' | 'medium' | 'slow'
  quality: 'good' | 'great' | 'best'
}

export const CLAUDE_MODELS: ModelOption[] = [
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Haiku',
    description: 'Fast & efficient',
    speed: 'fast',
    quality: 'good',
  },
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Sonnet',
    description: 'Balanced performance',
    speed: 'medium',
    quality: 'great',
  },
  {
    id: 'claude-opus-4-20250514',
    name: 'Opus',
    description: 'Most capable',
    speed: 'slow',
    quality: 'best',
  },
]

// ============================================================================
// EMBEDDING PROVIDERS
// ============================================================================

export type EmbeddingProvider = 'openai' | 'mistral' | 'ollama'

export interface EmbeddingProviderOption {
  id: EmbeddingProvider
  name: string
  description: string
  model: string
  dimensions: number
  requiresApiKey: boolean
  rateLimit: string
}

// All providers standardized to 1024 dimensions for cross-provider compatibility
export const EMBEDDING_PROVIDERS: EmbeddingProviderOption[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Fast, reliable cloud API',
    model: 'text-embedding-3-small',
    dimensions: 1024, // Uses dimensions param
    requiresApiKey: true,
    rateLimit: '3,000 RPM',
  },
  {
    id: 'mistral',
    name: 'Mistral',
    description: 'High quality embeddings',
    model: 'mistral-embed',
    dimensions: 1024, // Native 1024d
    requiresApiKey: true,
    rateLimit: '500+ RPM',
  },
  {
    id: 'ollama',
    name: 'Ollama (Local)',
    description: 'No rate limits, runs locally',
    model: 'mxbai-embed-large',
    dimensions: 1024, // Native 1024d
    requiresApiKey: false,
    rateLimit: 'Unlimited',
  },
]

interface AIState {
  apiKey: string
  isConfigured: boolean
  // Model preferences for different functions
  documentAnalysisModel: ClaudeModel
  coachingModel: ClaudeModel
  // Coaching style (legacy v2 feature)
  coachingStyle: CoachingStyle
  // Conversation mode (new conversational approach)
  conversationMode: ConversationMode
  persistFacets: boolean
  // Embedding provider configuration
  embeddingProvider: EmbeddingProvider
  openaiApiKey: string
  mistralApiKey: string
  ollamaUrl: string
  // Actions
  setApiKey: (key: string) => void
  clearApiKey: () => void
  setDocumentAnalysisModel: (model: ClaudeModel) => void
  setCoachingModel: (model: ClaudeModel) => void
  setCoachingStyle: (style: CoachingStyle) => void
  setConversationMode: (mode: ConversationMode) => void
  setPersistFacets: (persist: boolean) => void
  setEmbeddingProvider: (provider: EmbeddingProvider) => void
  setOpenAIApiKey: (key: string) => void
  setMistralApiKey: (key: string) => void
  setOllamaUrl: (url: string) => void
  getEmbeddingConfig: () => { provider: EmbeddingProvider; apiKey?: string; baseUrl?: string } | null
}

export const useAIStore = create<AIState>()(
  persist(
    (set, get) => ({
      apiKey: '',
      isConfigured: false,
      // Default: Sonnet for document analysis (smart), Haiku for coaching (fast)
      documentAnalysisModel: 'claude-sonnet-4-20250514',
      coachingModel: 'claude-haiku-4-5-20251001',
      // Coaching style: collaborative for warm coaching experience (legacy)
      coachingStyle: 'collaborative',
      // Conversation mode: organic for natural flow (new approach)
      conversationMode: 'organic',
      persistFacets: true,
      // Embedding provider defaults - Ollama for development (no rate limits)
      embeddingProvider: 'ollama',
      openaiApiKey: '',
      mistralApiKey: '',
      ollamaUrl: 'http://localhost:11434',

      setApiKey: (key: string) => {
        set({ apiKey: key, isConfigured: key.length > 0 })
      },

      clearApiKey: () => {
        set({ apiKey: '', isConfigured: false })
      },

      setDocumentAnalysisModel: (model: ClaudeModel) => {
        set({ documentAnalysisModel: model })
      },

      setCoachingModel: (model: ClaudeModel) => {
        set({ coachingModel: model })
      },

      setCoachingStyle: (style: CoachingStyle) => {
        set({ coachingStyle: style })
      },

      setConversationMode: (mode: ConversationMode) => {
        set({ conversationMode: mode })
      },

      setPersistFacets: (persist: boolean) => {
        set({ persistFacets: persist })
      },

      setEmbeddingProvider: (provider: EmbeddingProvider) => {
        set({ embeddingProvider: provider })
      },

      setOpenAIApiKey: (key: string) => {
        set({ openaiApiKey: key })
      },

      setMistralApiKey: (key: string) => {
        set({ mistralApiKey: key })
      },

      setOllamaUrl: (url: string) => {
        set({ ollamaUrl: url })
      },

      getEmbeddingConfig: () => {
        const state = get()
        switch (state.embeddingProvider) {
          case 'openai':
            if (!state.openaiApiKey) return null
            return { provider: 'openai' as const, apiKey: state.openaiApiKey }
          case 'mistral':
            if (!state.mistralApiKey) return null
            return { provider: 'mistral' as const, apiKey: state.mistralApiKey }
          case 'ollama':
            return { provider: 'ollama' as const, baseUrl: state.ollamaUrl }
          default:
            return null
        }
      },
    }),
    {
      name: 'intellispot-ai',
      partialize: (state) => ({
        apiKey: state.apiKey,
        isConfigured: state.isConfigured,
        documentAnalysisModel: state.documentAnalysisModel,
        coachingModel: state.coachingModel,
        coachingStyle: state.coachingStyle,
        conversationMode: state.conversationMode,
        persistFacets: state.persistFacets,
        embeddingProvider: state.embeddingProvider,
        openaiApiKey: state.openaiApiKey,
        mistralApiKey: state.mistralApiKey,
        ollamaUrl: state.ollamaUrl,
      }),
    }
  )
)
